import { type ReactNode } from "react";
import MuiButton from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import type { HealthStatus } from "../lib/types";
import { useI18n } from "../lib/i18n";

const STATUS_COLOR: Record<HealthStatus, "success" | "error" | "default"> = {
  healthy: "success",
  unhealthy: "error",
  unknown: "default",
};

/** Nhãn trạng thái sức khoẻ của link — dùng chung ở mọi bảng. */
export function Badge({ status }: { status: HealthStatus }) {
  const { t } = useI18n();
  return <Chip size="small" color={STATUS_COLOR[status]} label={t(`status.${status}`)} />;
}

/** Ánh xạ variant nội bộ (cũ) sang variant/color của MUI để không phải sửa mọi trang. */
const BUTTON_VARIANT = {
  default: { variant: "outlined", color: "inherit" },
  primary: { variant: "contained", color: "primary" },
  danger: { variant: "contained", color: "error" },
  ghost: { variant: "text", color: "inherit" },
} as const;

export function Button({
  children,
  variant = "default",
  loading,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "default" | "primary" | "danger" | "ghost";
  loading?: boolean;
}) {
  const v = BUTTON_VARIANT[variant];
  return (
    <MuiButton
      {...props}
      variant={v.variant}
      color={v.color}
      disabled={props.disabled || loading}
      startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
    >
      {children}
    </MuiButton>
  );
}

/** Khối nội dung có tiêu đề + vùng thao tác — thay cho `.card` tự viết. */
export function Card({
  title,
  actions,
  children,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0 }}
    >
      {(title || actions) && (
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h2" component="h2">
            {title}
          </Typography>
          {actions && (
            <Stack direction="row" sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              {actions}
            </Stack>
          )}
        </Stack>
      )}
      {children}
    </Paper>
  );
}

const STAT_TONE = {
  neutral: "text.primary",
  good: "success.main",
  bad: "error.main",
  warn: "warning.main",
} as const;

/** Ô thống kê lớn (tổng/khoẻ/lỗi…) trên trang Tổng quan. */
export function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  tone?: "neutral" | "good" | "bad" | "warn";
}) {
  return (
    <Paper variant="outlined" sx={{ px: 2, py: 1.75 }}>
      <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2, color: STAT_TONE[tone] }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: "block" }}>
        {label}
      </Typography>
    </Paper>
  );
}

/** Hộp thoại dùng chung — bọc MUI Dialog nhưng giữ API cũ (title/onClose/footer). */
export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const closeLabel = useI18n().t("common.close");
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-label={title}>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}
      >
        <Typography variant="h3" component="span">
          {title}
        </Typography>
        <IconButton onClick={onClose} aria-label={closeLabel} edge="end">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
        {children}
      </DialogContent>
      {footer && <DialogActions sx={{ px: 2.25, py: 1.5, gap: 1 }}>{footer}</DialogActions>}
    </Dialog>
  );
}

/**
 * Bọc một control với nhãn + gợi ý. Giữ lại cho các trường hợp không phải TextField
 * (input file, ô tuỳ biến). Với input/select thường, ưu tiên dùng MUI TextField.
 */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    // className="field" để CSS `.field input/select/textarea` (index.css) áp dụng —
    // đây là styling chuyên nghiệp cho các control tự viết (không phải MUI TextField).
    <Box className="field" sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, color: "text.secondary", fontSize: 12.5 }}
      >
        {label}
      </Typography>
      {children}
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

/** Trạng thái rỗng của một bảng/danh sách. */
export function Empty({ message }: { message: string }) {
  return (
    <Box sx={{ p: 3.5, textAlign: "center", color: "text.secondary", fontSize: 13 }}>{message}</Box>
  );
}

/** Thông báo lỗi dạng dải màu đỏ. */
export function ErrorBox({ message }: { message: string }) {
  return (
    <Alert severity="error" variant="outlined" sx={{ alignItems: "center" }}>
      {message}
    </Alert>
  );
}

/** Chỉ báo đang tải — spinner + nhãn. */
export function Loading({ label }: { label?: string }) {
  const { t } = useI18n();
  return (
    <Box
      sx={{
        p: 3.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        color: "text.secondary",
        fontSize: 13,
      }}
    >
      <CircularProgress size={16} color="inherit" />
      {label ?? t("common.loading")}
    </Box>
  );
}
